// CORE
import * as React from 'react';
import { useCallback, useContext } from 'react';

import InfoIcon from '@mui/icons-material/Info';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
// MUI
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import QButton from 'components/QButton';
// SHARED/LOCAL
import AmountField from 'components/QuickenControls/AmountField';
import { AddGoalContext } from './state';

const useStyles = makeStyles()((theme) => ({
  root: {
    padding: '16px 24px 24px 16px',
    minHeight: 448,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  leftPanel: {
    height: 128,
    width: 'auto',
  },
  rightPanel: {
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: 3,
    padding: theme.spacing(2),
    paddingRight: theme.spacing(5),
  },
  amountContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
    background: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius * 2,
    margin: `0 ${theme.spacing(1)}`,
  },
  radioGroup: {
    flexDirection: 'row',
  },
  infoContainer: {
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.default,
  },
  divider: {
    height: 1,
    width: '80%',
    background: theme.palette.border.light,
  },
  errorText: {
    color: theme.palette.error.main,
    alignItems: 'center',
  },
  errorState: {
    display: 'grid',
    justifyItems: 'center',
  },
}));

const GoalFinalize = (props: { error: boolean }) => {
  const { error } = props;

  const { dispatch, state, onSubmit } = useContext(AddGoalContext);
  const { type, targetAmount, contributionAmount, targetDate, includeInF2S } = state;
  const { classes } = useStyles();

  const handleIncludeInF2SChange = useCallback(
    // @ts-expect-error FIXME TS7006
    (e) => {
      dispatch({ type: 'update', payload: { includeInF2S: e.target.value === 'yes' } });
    },
    [dispatch],
  );

  return (
    <div className={classes.root}>
      <Box display='flex'>
        <img className={classes.leftPanel} alt='goal-icon' src={type.icon} />
        <div className={classes.rightPanel}>
          <Typography variant='bodyRegular'>Confirm your goal details.</Typography>
          <Box marginTop={1} display='flex' alignItems='center'>
            <div className={classes.amountContainer}>
              <Typography variant='paragraphRegular'>Goal amount</Typography>
              <AmountField variant='titleRegular' value={targetAmount} />
            </div>
            <div className={classes.amountContainer}>
              <Typography variant='paragraphRegular'>Monthly contribution</Typography>
              <AmountField variant='titleRegular' value={contributionAmount} />
            </div>
            <div className={classes.amountContainer}>
              <Typography variant='paragraphRegular'>Goal ends on</Typography>
              <Typography>{targetDate?.toFormat('MMMM yyyy') || 'Not set'}</Typography>
            </div>
          </Box>

          <Box marginTop={2} marginBottom={2}>
            <Typography variant='bodyRegular'>Include planned contribution in spending plan</Typography>
            <FormControl component='fieldset'>
              <RadioGroup
                className={classes.radioGroup}
                name={'include-contribution-in-spending-plan'}
                value={includeInF2S ? 'yes' : 'no'}
                onChange={handleIncludeInF2SChange}
              >
                <FormControlLabel
                  value={'yes'}
                  control={<Radio id='create_goal_yes_subtract_from_spending_plan_radio' />}
                  label={'Yes, set aside.'}
                />
                <FormControlLabel
                  value={'no'}
                  control={<Radio id='create_goal_no_subtract_from_spending_plan_radio' />}
                  label={"No, don't set aside."}
                />
              </RadioGroup>
            </FormControl>
          </Box>

          <div className={classes.divider} />

          <Box marginTop={4} alignItems='center' padding={1} display='flex' className={classes.infoContainer}>
            <Box marginRight={1}>
              <InfoIcon />
            </Box>
            <Box>
              <Typography variant='paragraphRegular'>
                Simplifi will never add or take money from your actual financial accounts.
              </Typography>
              <Typography variant='paragraphRegular'>
                Your goal is meant to keep you accountable and moving forward!
              </Typography>
            </Box>
          </Box>
        </div>
      </Box>
      <Box marginTop={3} display='flex' justifyContent='flex-end'>
        <div className={classes.errorState}>
          {error && (
            <Typography variant='captionRegular' className={classes.errorText}>
              Error occurred
            </Typography>
          )}
          <QButton id='create_goal_continue_button' onClick={onSubmit} variant='contained'>
            create goal
          </QButton>
        </div>
      </Box>
    </div>
  );
};

export default GoalFinalize;
